/**
 * ChapterRow Component
 * Displays a single chapter in the chapters list with tier badge, status, and actions
 */
import { Link } from "react-router-dom";
import type { ChapterListItem } from "../lib/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChapterRowProps {
  chapter: ChapterListItem;
  onDelete: (id: string) => void;
}

// ---------------------------------------------------------------------------
// Tier Badge Helper
// ---------------------------------------------------------------------------

/**
 * Get tier badge styling and label based on tierId
 * TODO: Phase 3 - Fetch actual tier names from API and display them
 */
function getTierBadge(tierId: string | null): { color: string; label: string } {
  if (tierId === null) {
    return { color: "bg-gray-100 text-gray-700", label: "Free" };
  }
  // TODO: Phase 3 - Look up tier name from tierId via API/context
  return { color: "bg-purple-100 text-purple-700", label: "Custom" };
}

// ---------------------------------------------------------------------------
// Utility Functions
// ---------------------------------------------------------------------------

/**
 * Format a number with commas for thousands
 */
function formatNumber(num: number): string {
  return num.toLocaleString();
}

/**
 * Format a date for display
 */
function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ChapterRow({ chapter, onDelete }: ChapterRowProps) {
  const handleDelete = () => {
    if (!confirm("Are you sure you want to delete this chapter?")) return;
    onDelete(chapter.id);
  };

  return (
    <div
      data-testid="chapter-row"
      className="flex items-center justify-between px-4 py-3 border-b border-slate-200 last:border-b-0 hover:bg-slate-50 transition-colors"
    >
      {/* Chapter Number */}
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <span className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-full text-sm font-medium text-slate-600">
          {chapter.chapterNumber}
        </span>

        {/* Title and Meta */}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-slate-900 truncate">
            {chapter.title}
          </h3>
          <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
            <span>{formatNumber(chapter.wordCount)} words</span>
          </div>
        </div>
      </div>

      {/* Tier Badge */}
      <div className="flex items-center gap-4">
        {(() => {
          const badge = getTierBadge(chapter.tierId);
          return (
            <span
              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}
            >
              {badge.label}
            </span>
          );
        })()}

        {/* Published Status */}
        <span className="w-24 text-sm text-slate-500 text-right">
          {chapter.publishedAt ? formatDate(chapter.publishedAt) : "Draft"}
        </span>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Link
            to={`/novels/${chapter.novelId}/chapters/${chapter.id}/edit`}
            className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition-colors"
          >
            Edit
          </Link>
          <button
            onClick={handleDelete}
            className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
